import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';

// Enums from API documentation
export type StreakStatus = 'ACTIVE' | 'BROKEN' | 'FROZEN' | 'AT_RISK';
export type FreezePeriod = 1 | 3 | 7;
export type StreakMilestone = 7 | 14 | 30 | 50 | 100 | 365;

// API Response Types
export interface CurrentStreakDto {
  current_streak: number;
  longest_streak: number;
  last_activity_date: string; // ISO 8601 datetime
  streak_frozen: boolean;
  freeze_days_remaining: number;
  next_milestone: number;
  total_learning_days: number;
  streak_status: StreakStatus;
  at_risk: boolean;
  hours_until_reset?: number;
}

export interface StreakLeaderboardItemDto {
  rank: number;
  user_id: string;
  user_name: string;
  current_streak: number;
  longest_streak: number;
  total_learning_days: number;
  avatar_url?: string | null;
}

export interface FreezeStreakRequestDto {
  days: FreezePeriod; // 1-7
}

export interface FreezeStreakResponseData {
  success: boolean;
  message: string;
  data: {
    streak_frozen: boolean;
    freeze_end_date: string; // ISO 8601 datetime
    freeze_tokens_remaining: number;
  };
}

export interface UpdateLearningActivityDto {
  user_id: string;
  minutes_learned: number; // ≥1
  courses_accessed?: string[];
  modules_completed?: string[];
}

export interface StreakApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  error?: string;
  error_description?: string;
}

export interface LeaderboardFilters {
  scope: 'global' | 'department';
  limit?: number; // 1-100, default: 10
  offset?: number; // ≥0, default: 0
}

class LearningStreakService {
  /**
   * Get current user's streak information
   */
  async getCurrentStreak(): Promise<CurrentStreakDto> {
    try {
      const response = await apiClient.get<StreakApiResponse<CurrentStreakDto>>(
        API_ENDPOINTS.LEARNING_STREAK.CURRENT
      );
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.error_description || 'Failed to fetch current streak');
    } catch (error) {
      console.error('Error fetching current streak:', error);
      throw error;
    }
  }

  /**
   * Get streak leaderboard
   */
  async getLeaderboard(filters: LeaderboardFilters): Promise<StreakLeaderboardItemDto[]> {
    try {
      const params = new URLSearchParams();
      
      params.set('scope', filters.scope);
      if (filters.limit) params.set('limit', filters.limit.toString());
      if (filters.offset) params.set('offset', filters.offset.toString());
      
      const response = await apiClient.get<StreakApiResponse<StreakLeaderboardItemDto[]>>(
        `${API_ENDPOINTS.LEARNING_STREAK.LEADERBOARD}?${params.toString()}`
      );
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.error_description || 'Failed to fetch leaderboard');
    } catch (error) {
      console.error('Error fetching streak leaderboard:', error);
      throw error;
    }
  }

  /**
   * Freeze current streak
   */
  async freezeStreak(days: FreezePeriod): Promise<FreezeStreakResponseData> {
    try {
      const request: FreezeStreakRequestDto = { days };
      
      const response = await apiClient.post<StreakApiResponse<FreezeStreakResponseData>>(
        API_ENDPOINTS.LEARNING_STREAK.FREEZE,
        request
      );
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.error_description || 'Failed to freeze streak');
    } catch (error) {
      console.error('Error freezing streak:', error);
      throw error;
    }
  }

  /**
   * Update learning activity to maintain streak
   */
  async updateActivity(activity: UpdateLearningActivityDto): Promise<{ success: boolean }> {
    try {
      const response = await apiClient.post<StreakApiResponse<{ success: boolean }>>(
        API_ENDPOINTS.LEARNING_STREAK.UPDATE_ACTIVITY,
        activity
      );
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.error_description || 'Failed to update learning activity');
    } catch (error) {
      console.error('Error updating learning activity:', error);
      throw error;
    }
  }

  /**
   * Get user streak by ID (Admin only)
   */
  async getUserStreak(userId: string): Promise<CurrentStreakDto> {
    try {
      const response = await apiClient.get<StreakApiResponse<CurrentStreakDto>>(
        API_ENDPOINTS.LEARNING_STREAK.GET_USER_STREAK(userId)
      );
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.error_description || 'Failed to fetch user streak');
    } catch (error) {
      console.error('Error fetching user streak:', error);
      throw error;
    }
  }

  /**
   * Process daily streak updates (Admin/System only)
   */
  async processDailyUpdates(): Promise<{ success: boolean }> {
    try {
      const response = await apiClient.post<StreakApiResponse<{ success: boolean }>>(
        API_ENDPOINTS.LEARNING_STREAK.ADMIN_PROCESS_DAILY
      );
      
      if (response.data.success) {
        return response.data.data;
      }
      
      throw new Error(response.data.error_description || 'Failed to process daily updates');
    } catch (error) {
      console.error('Error processing daily updates:', error);
      throw error;
    }
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Format streak status for display
   */
  getStreakStatusLabel(status: StreakStatus): string {
    const labels: Record<StreakStatus, string> = {
      ACTIVE: 'Active',
      BROKEN: 'Broken',
      FROZEN: 'Frozen',
      AT_RISK: 'At Risk'
    };
    return labels[status];
  }

  /**
   * Get streak status color for UI
   */
  getStreakStatusColor(status: StreakStatus): string {
    const colors: Record<StreakStatus, string> = {
      ACTIVE: 'text-green-600 bg-green-50',
      BROKEN: 'text-red-600 bg-red-50',
      FROZEN: 'text-blue-600 bg-blue-50',
      AT_RISK: 'text-yellow-600 bg-yellow-50'
    };
    return colors[status];
  }

  /**
   * Check if milestone is achieved
   */
  isMilestoneAchieved(currentStreak: number, milestone: StreakMilestone): boolean {
    return currentStreak >= milestone;
  }

  /**
   * Get next milestone
   */
  getNextMilestone(currentStreak: number): StreakMilestone | null {
    const milestones: StreakMilestone[] = [7, 14, 30, 50, 100, 365];
    return milestones.find(milestone => milestone > currentStreak) || null;
  }

  /**
   * Calculate progress to next milestone
   */
  getMilestoneProgress(currentStreak: number): { current: number; next: StreakMilestone | null; percentage: number } {
    const nextMilestone = this.getNextMilestone(currentStreak);
    
    if (!nextMilestone) {
      return { current: currentStreak, next: null, percentage: 100 };
    }
    
    const previousMilestone = [7, 14, 30, 50, 100, 365].reverse().find(m => m <= currentStreak) || 0;
    const progress = ((currentStreak - previousMilestone) / (nextMilestone - previousMilestone)) * 100;
    
    return {
      current: currentStreak,
      next: nextMilestone,
      percentage: Math.min(Math.max(progress, 0), 100)
    };
  }

  /**
   * Format time until reset
   */
  formatTimeUntilReset(hours?: number): string {
    if (!hours) return 'Unknown';
    
    if (hours < 1) {
      const minutes = Math.floor(hours * 60);
      return `${minutes} minute${minutes === 1 ? '' : 's'}`;
    }
    
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = Math.floor(hours % 24);
      return `${days}d ${remainingHours}h`;
    }
    
    return `${Math.floor(hours)} hour${Math.floor(hours) === 1 ? '' : 's'}`;
  }

  /**
   * Check if streak is at risk (< 12 hours until reset)
   */
  isStreakAtRisk(hoursUntilReset?: number): boolean {
    return hoursUntilReset !== undefined && hoursUntilReset < 12;
  }

  /**
   * Get freeze period options
   */
  getFreezePeriodOptions(): Array<{ value: FreezePeriod; label: string }> {
    return [
      { value: 1, label: '1 Day' },
      { value: 3, label: '3 Days' },
      { value: 7, label: '7 Days' }
    ];
  }

  /**
   * Validate freeze request
   */
  canFreezeStreak(streak: CurrentStreakDto): { canFreeze: boolean; reason?: string } {
    if (streak.streak_frozen) {
      return { canFreeze: false, reason: 'Streak is already frozen' };
    }
    
    if (streak.streak_status === 'BROKEN') {
      return { canFreeze: false, reason: 'Cannot freeze a broken streak' };
    }
    
    // Assuming users get 2 freeze tokens per month (business rule from docs)
    // This would need to be tracked in the API response, but not provided in current schema
    return { canFreeze: true };
  }
}

// Export singleton instance
export const learningStreakService = new LearningStreakService();
export default learningStreakService;